import type { Area, Point } from 'react-easy-crop';

import Cropper from 'react-easy-crop';
import React, { useMemo, useState, useEffect, useCallback } from 'react';

import Slider from '@mui/material/Slider';
import { Box, Stack } from '@mui/material';

import type { ThumbnailArea } from './types';

function convertAreaToThumbnailArea(area: Area): ThumbnailArea {
  return {
    x1: area.x / 100,
    y1: area.y / 100,
    x2: (area.x + area.width) / 100,
    y2: (area.y + area.height) / 100,
  };
}

function convertThumbnailAreaToArea(thumbnailArea: ThumbnailArea): Area | undefined {
  const { x1, y1, x2, y2 } = thumbnailArea;
  if (![x1, y1, x2, y2].every((n) => typeof n === 'number' && Number.isFinite(n))) {
    return undefined;
  }
  const width = (x2 - x1) * 100;
  const height = (y2 - y1) * 100;
  // react-easy-crop divides by these; zero/negative values crash or produce NaN.
  if (width <= 1e-6 || height <= 1e-6) {
    return undefined;
  }
  return {
    x: x1 * 100,
    y: y1 * 100,
    width,
    height,
  };
}

interface Props {
  image: File | string;
  aspect: number;
  initialCroppedArea: ThumbnailArea | null;
  onCropComplete: (aoi: ThumbnailArea) => void;
  disabled?: boolean;
}

export function ImageCrop({ image, aspect, initialCroppedArea, onCropComplete, disabled }: Props) {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);

  // Derive the URL in an effect so the Cropper receives it only after the
  // component is mounted and the container has settled dimensions
  const [imageUrl, setImageUrl] = useState<string>('');
  useEffect(() => {
    const url = image instanceof File ? URL.createObjectURL(image) : image;
    setImageUrl(url);
    if (image instanceof File) {
      return () => URL.revokeObjectURL(url);
    }
    return undefined;
  }, [image]);

  // Memoize the initial cropped area
  const initialCroppedAreaPercentages = useMemo(
    () => (initialCroppedArea ? convertThumbnailAreaToArea(initialCroppedArea) : undefined),
    [initialCroppedArea]
  );

  // Use useCallback for event handlers
  const handleCropComplete = useCallback(
    (aoi: any, _: Area) => onCropComplete(convertAreaToThumbnailArea(aoi)),
    [onCropComplete]
  );

  const handleZoomChange = useCallback((newZoom: number) => {
    setZoom(newZoom);
  }, []);

  const handleSliderChange = useCallback((e: Event, newZoom: number | number[]) => {
    setZoom(Number(newZoom));
  }, []);

  return (
    <Stack sx={{ opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
      <Slider
        value={zoom}
        min={1}
        max={3}
        step={0.1}
        aria-labelledby="Zoom"
        onChange={handleSliderChange}
        disabled={disabled}
        sx={{
          width: '90%',
          margin: '20px auto',
          '& .MuiSlider-thumb': {
            width: 20,
            height: 20,
            '&:hover': {
              boxShadow: '0 0 0 8px rgba(25, 118, 210, 0.16)',
            },
          },
          '& .MuiSlider-track': {
            height: 8,
          },
          '& .MuiSlider-rail': {
            height: 8,
            opacity: 0.5,
          },
        }}
      />
      <Box sx={{ position: 'relative', width: '100%', height: '100%', minHeight: 400 }}>
        <Cropper
          image={imageUrl}
          crop={crop}
          zoom={zoom}
          aspect={aspect}
          onCropChange={setCrop}
          onCropComplete={handleCropComplete}
          onZoomChange={handleZoomChange}
          initialCroppedAreaPercentages={initialCroppedAreaPercentages}
        />
      </Box>
    </Stack>
  );
}
